// Plain stand-in for MUI <Tooltip placement="top-start" arrow>: shows the full title on hover or
// keyboard focus after the same 100ms enter delay. Rendered only while open, like MUI's portal,
// so the title text is not duplicated in the server HTML.
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import styles from './LatestInsights.module.scss';

const ENTER_DELAY = 100;

interface TitleTooltipProps {
    title: string;
    children: ReactNode;
}

const TitleTooltip = ({title, children}: TitleTooltipProps) => {
    const [open, setOpen] = useState(false);
    const [anchorWidth, setAnchorWidth] = useState(0);
    const anchorRef = useRef<HTMLDivElement>(null);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const show = useCallback(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            setAnchorWidth(anchorRef.current?.offsetWidth ?? 0);
            setOpen(true);
        }, ENTER_DELAY);
    }, []);

    const hide = useCallback(() => {
        clearTimeout(timer.current);
        setOpen(false);
    }, []);

    useEffect(() => () => clearTimeout(timer.current), []);

    // Popper centres the arrow on the anchor, clamped inside the tooltip box.
    const arrowStyle = {'--tooltip-arrow-left': `${anchorWidth / 2}px`} as CSSProperties;

    return (
        <div
            ref={anchorRef}
            className={styles.tooltipAnchor}
            aria-label={title}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            {children}
            {open && (
                <div className={styles.tooltipPopper} role="tooltip" style={arrowStyle}>
                    <div className={styles.tooltip}>
                        {title}
                        <span className={styles.tooltipArrow} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(TitleTooltip);
